import React from 'react';
import { useTranslation } from 'react-i18next';
import DialogBox from '../../../core-components/theme/components/DialogBox/DialogBox';
import Typography from '@mui/material/Typography';
import { Box, Grid } from '@mui/material';
import { EmailPreviewDialogProps } from '../types/types';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import { handleDownload } from '../helpers/helper';
import { emailPreviewDialogStyles } from './styles';
import { DynamicCard } from '../../../core-components/theme/components/DynamicCard';
import { Neutrals } from '../../../core-components/theme/color-palette/colors';

const EmailPreviewDialog: React.FC<EmailPreviewDialogProps> = ({ open, onClose, communication }) => {
  const { t } = useTranslation();
  return (
    <DialogBox
      open={open}
      onClose={onClose}
      title={t('communication.email', 'Email')}
      showCloseIcon 
      minHeight="30%" 
      minWidth="40%"
    >
      <Box sx={{...emailPreviewDialogStyles.container}}>
        <Typography variant="subtitle2" sx={emailPreviewDialogStyles.subjectLabel}>
          {t('communication.subject', 'Subject:')}
        </Typography>
        <Typography variant="body2" sx={emailPreviewDialogStyles.subjectText}>
          {communication?.messageContent || t('communication.noSubject', 'No Subject')}
        </Typography>

        <Typography variant="subtitle2" sx={emailPreviewDialogStyles.messageLabel}>
          {t('communication.message', 'Message:')}
        </Typography>
        <Typography 
          variant="body2" 
          sx={emailPreviewDialogStyles.messageText}
          dangerouslySetInnerHTML={{ __html: communication?.emailBody || "" }}
        />

        {communication?.attachments && communication.attachments.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={emailPreviewDialogStyles.attachmentsLabel}>
              {t('communication.attachments', 'Attachments:')}
            </Typography>
            <Grid container spacing={2}>
              {communication.attachments.map((attachment: any, index: number) => (
                <Grid item xs={6} sm={4} md={3} key={index} >
                  <DynamicCard
                    title={attachment.name}
                    width="100%"
                    height={"100%"}
                    elevation={0}
                    borderRadius={2}
                    backgroundColor="#ffffff"
                    clickable
                    onClick={() => handleDownload(attachment)}
                    icon={{
                      component: DownloadIcon,
                      color: Neutrals[300],
                      size: 24,
                    }}
                    sx={{
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        borderColor: '#1976d2',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    contentSx={{
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      '& .MuiTypography-h6': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                        minHeight: '2.6rem',
                        pr: 3,
                        mt: 'auto',
                      },
                    }}
                    children={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 100,
                          backgroundColor: '#f8f9fa',
                          borderRadius: 1,
                          mb: 1.5,
                        }}
                      >
                        <PictureAsPdfIcon
                          sx={{
                            fontSize: 48,
                            color: '#d32f2f',
                          }}
                        />
                      </Box>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </DialogBox>
  );
};

export default EmailPreviewDialog; 
